import { useCallback, useEffect, useState, useMemo } from 'react';

import { QuickActionLogicProps } from '@components/suggestions/quickActions/types';

import { EmailMessageDto } from '@/api/generated';
import useEmail from '@/hooks/useEmail';
import { launchErrorToast } from '@/utils/toast';

import useSetAction from './useSetAction';

const useTaskFollowUpQuickActionLogic = ({ quickAction, index }: QuickActionLogicProps) => {
  const [provider, setProvider] = useState<string>('Email');
  const [emailMessage, setEmailMessage] = useState<string>('');
  const [emailSubject, setEmailSubject] = useState<string>('');

  const {
    emailMessage: emailMessageObject,
    emailMessageLoading,
    emailMessageError,
    generateDelegateFollowUpMessage,
    isGenerateDelegateFollowUpMessagePending,
    isGenerateDelegateFollowUpMessageError,
    sendEmailMessage,
    getThreadContent,
    getLastEmailBody,
    emailThreadMessagesLoading,
    emailThreadMessagesError
  } = useEmail(quickAction.sourceId || '');

  const emailBody = useMemo(() => getLastEmailBody(), [getLastEmailBody, emailMessageObject]);

  const actionFunction = useCallback(
    async (): Promise<EmailMessageDto | undefined> =>
      sendEmailMessage({
        to: emailMessageObject?.to?.map(user => user.email || '') || [],
        subject: emailSubject,
        body: emailMessage
      }),
    [emailMessage, emailSubject, emailMessageObject?.to, sendEmailMessage]
  );

  useSetAction(
    actionFunction,
    index,
    !emailMessageObject?.id ||
      isGenerateDelegateFollowUpMessagePending ||
      emailMessageLoading ||
      isGenerateDelegateFollowUpMessageError ||
      emailMessageError ||
      emailThreadMessagesLoading ||
      emailThreadMessagesError
  );

  const threadContent = useMemo(() => getThreadContent(), [getThreadContent]);

  const reGenerateMessage = useCallback(() => {
    generateDelegateFollowUpMessage({
      taskTitle: emailMessageObject?.subject || '',
      assigneeName: emailMessageObject?.to?.[0]?.name || '',
      delegationContext: threadContent
    }).then(response => {
      setEmailMessage(response?.message || '');
    });
  }, [generateDelegateFollowUpMessage, emailMessageObject, threadContent, setEmailMessage]);

  useEffect(() => {
    if (!emailMessageLoading && threadContent) {
      reGenerateMessage();
    }
    setEmailSubject(emailMessageObject?.subject || '');
  }, [emailMessageObject, reGenerateMessage, threadContent, emailMessageLoading]);

  useEffect(() => {
    if (emailMessageError || isGenerateDelegateFollowUpMessageError || emailThreadMessagesError) {
      launchErrorToast();
    }
  }, [emailMessageError, isGenerateDelegateFollowUpMessageError, emailThreadMessagesError]);

  const bubbleTextUsers = useMemo(
    () => `${emailMessageObject?.to?.map(user => user.name || '').join(', ')}`,
    [emailMessageObject]
  );

  return {
    emailMessageObject,
    emailSubject,
    emailMessage,
    setEmailSubject,
    setEmailMessage,
    emailMessageLoading,
    emailMessageError,
    provider,
    setProvider,
    reGenerateMessage,
    isGenerateDelegateFollowUpMessagePending,
    emailBody,
    bubbleTextUsers
  };
};

export default useTaskFollowUpQuickActionLogic;
